import { useState, useEffect, useCallback } from "react";
import api, { apiError } from "../../lib/api";
import { Card, Loading, inputCls, labelCls, btnPrimary, btnSecondary } from "../../components/admin/common";
import { RefreshCw, Copy, ChevronDown, ChevronUp, ShieldCheck, AlertTriangle, MousePointerClick, Reply, ExternalLink, Save } from "lucide-react";
import { toast } from "sonner";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" }); } catch { return iso; }
}
const MSG_STATUS = { sent: "bg-slate-100 text-slate-600", delivered: "bg-sky-100 text-sky-700", read: "bg-emerald-100 text-emerald-700", failed: "bg-rose-100 text-rose-700" };
const SOURCES = [["", "All sources"], ["bulk", "Bulk campaigns"], ["reminder", "Payment reminders"], ["confirmation", "Payment confirmations"], ["test", "Test sends"]];

function CopyRow({ label, value, testid }) {
  return (
    <div><div className={labelCls}>{label}</div>
      <div className="flex gap-2">
        <input readOnly className={`${inputCls} font-mono text-xs`} value={value} data-testid={testid} />
        <button className={`${btnSecondary} px-3`} onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }} data-testid={`${testid}-copy`}><Copy className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function TrackingSetup() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState("");
  const [fwd, setFwd] = useState("");
  const load = useCallback(async () => {
    try { const { data } = await api.get("/whatsapp/webhook-config"); setCfg(data); setFwd(data.forward_url || ""); setOpen((o) => o || !data.app_secret_set); } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { const { data } = await api.put("/whatsapp/webhook-config", { app_secret: secret || null, forward_url: fwd }); setCfg(data); setSecret(""); toast.success("Tracking settings saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (!cfg) return null;
  const live = cfg.app_secret_set && cfg.events_received > 0;
  return (
    <Card className="p-4" data-testid="tracking-setup-card">
      <button className="w-full flex items-center gap-3 text-left" onClick={() => setOpen(!open)} data-testid="tracking-setup-toggle">
        {live ? <ShieldCheck className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-amber-500" />}
        <div className="flex-1">
          <div className="font-semibold text-sm text-slate-800">Tracking connection {live ? "active" : "— setup needed"}</div>
          <div className="text-xs text-slate-500" data-testid="tracking-status-text">
            {cfg.events_received ? `${cfg.events_received} verified updates received from Meta · last ${fmt(cfg.last_event_at)}` : "No verified updates received from Meta yet."}
            {cfg.events_unverified > 0 && !cfg.events_received && ` · ${cfg.events_unverified} unverified (check App Secret)`}
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="mt-4 space-y-4 border-t border-slate-100 pt-4">
          <ol className="text-xs text-slate-600 list-decimal pl-4 space-y-1">
            <li>Paste your Meta <b>App Secret</b> below and save.</li>
            <li>In developers.facebook.com → app <b>GDIOT</b> → WhatsApp → Configuration → Webhook → <b>Edit</b>: paste the Callback URL and Verify token below, then <b>Verify and save</b>.</li>
            <li>Under Webhook fields, make sure <b>messages</b> and <b>message_template_status_update</b> are subscribed.</li>
            <li>Every update is passed on as an exact copy to the Forward URL, so your existing worker keeps working.</li>
          </ol>
          <div className="grid md:grid-cols-2 gap-4">
            <CopyRow label="Callback URL" value={`${window.location.origin}/api/whatsapp/webhook`} testid="webhook-callback-url" />
            <CopyRow label="Verify token" value={cfg.verify_token} testid="webhook-verify-token" />
            <div><label className={labelCls}>App Secret {cfg.app_secret_set && <span className="text-emerald-600 normal-case">(saved ✓)</span>}</label>
              <input type="password" className={inputCls} placeholder={cfg.app_secret_set ? "•••••••• (enter to replace)" : "32-character App Secret"} value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="webhook-app-secret-input" /></div>
            <div><label className={labelCls}>Forward copy to (existing worker)</label>
              <input className={inputCls} value={fwd} onChange={(e) => setFwd(e.target.value)} placeholder="Leave empty to stop forwarding" data-testid="webhook-forward-url-input" /></div>
          </div>
          <button className={btnPrimary} onClick={save} data-testid="webhook-config-save-btn"><Save className="w-4 h-4" /> Save tracking settings</button>
        </div>
      )}
    </Card>
  );
}

function Stat({ label, val, total, cls, testid }) {
  const pct = total ? Math.round((val / total) * 100) : 0;
  return (
    <Card className="p-4" data-testid={testid}>
      <div className={`text-2xl font-bold ${cls}`}>{val}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}{total && label !== "Sent" ? ` · ${pct}%` : ""}</div>
    </Card>
  );
}

const EMPTY_F = { template: "", campaign_id: "", source: "", status: "", button: "", date_from: "", date_to: "", q: "" };

export function WaAnalytics() {
  const [f, setF] = useState(EMPTY_F);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const { data: d } = await api.get("/whatsapp/analytics", { params: { ...params, skip: page * 50, limit: 50 } });
      setData(d);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [f, page]);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => { setPage(0); setF((p) => ({ ...p, [k]: v })); };
  const s = data?.summary;
  return (
    <div className="space-y-5">
      <TrackingSetup />
      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <select className={inputCls} value={f.template} onChange={(e) => set("template", e.target.value)} data-testid="analytics-template-filter">
            <option value="">All templates</option>{(data?.filters.templates || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className={inputCls} value={f.campaign_id} onChange={(e) => set("campaign_id", e.target.value)} data-testid="analytics-campaign-filter">
            <option value="">All campaigns</option>{(data?.filters.campaigns || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className={inputCls} value={f.source} onChange={(e) => set("source", e.target.value)} data-testid="analytics-source-filter">
            {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select className={inputCls} value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="analytics-status-filter">
            <option value="">All statuses</option><option value="delivered">Delivered</option><option value="read">Read</option><option value="unread">Not read</option>
            <option value="clicked">Clicked a button</option><option value="not_clicked">Did not click</option><option value="failed">Failed</option>
          </select>
          <select className={inputCls} value={f.button} onChange={(e) => set("button", e.target.value)} data-testid="analytics-button-filter">
            <option value="">All buttons</option>{(data?.buttons || []).map((b) => <option key={b.button_text + b.kind} value={b.button_text}>{b.button_text}</option>)}
          </select>
          <input type="date" className={inputCls} value={f.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="analytics-date-from" />
          <input type="date" className={inputCls} value={f.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="analytics-date-to" />
          <div className="flex gap-2">
            <input className={inputCls} placeholder="Search phone" value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="analytics-phone-search" />
            <button className={`${btnSecondary} px-3`} onClick={() => { setPage(0); setF(EMPTY_F); }} data-testid="analytics-clear-filters" title="Clear filters">✕</button>
            <button className={`${btnSecondary} px-3`} onClick={load} data-testid="analytics-refresh-btn"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>
      </Card>
      {!data ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Sent" val={s.sent} cls="text-slate-800" testid="stat-sent" />
            <Stat label="Delivered" val={s.delivered} total={s.sent} cls="text-sky-600" testid="stat-delivered" />
            <Stat label="Read" val={s.read} total={s.sent} cls="text-emerald-600" testid="stat-read" />
            <Stat label="Clicked" val={s.clicked} total={s.sent} cls="text-violet-600" testid="stat-clicked" />
            <Stat label="Failed" val={s.failed} total={s.total} cls="text-rose-600" testid="stat-failed" />
          </div>
          {data.buttons.length > 0 && (
            <Card className="p-4" data-testid="button-clicks-card">
              <div className="font-semibold text-sm text-slate-800 mb-3 flex items-center gap-2"><MousePointerClick className="w-4 h-4 text-violet-600" /> Button clicks</div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {data.buttons.map((b) => (
                  <button key={b.button_text + b.kind} onClick={() => set("button", f.button === b.button_text ? "" : b.button_text)}
                    className={`text-left rounded-xl border p-3 transition-colors ${f.button === b.button_text ? "border-violet-400 bg-violet-50" : "border-slate-200 hover:border-violet-300"}`} data-testid="button-click-tile">
                    <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">{b.kind === "url" ? <ExternalLink className="w-3.5 h-3.5" /> : <Reply className="w-3.5 h-3.5" />}{b.button_text || "(no label)"}</div>
                    <div className="text-xs text-slate-500 mt-1"><b className="text-violet-700">{b.unique}</b> people · {b.clicks} taps</div>
                  </button>
                ))}
              </div>
            </Card>
          )}
          <Card className="overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700 flex justify-between"><span>Messages ({data.total})</span></div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                  <tr>{["Phone", "Template", "Source", "Status", "Sent", "Delivered", "Read", "Clicks"].map((h) => <th key={h} className="px-4 py-2.5 text-left font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.items.map((m) => (
                    <tr key={m.id} className="hover:bg-slate-50 align-top" data-testid="analytics-message-row">
                      <td className="px-4 py-2.5 font-mono text-slate-700">{m.phone}{m.registration_id && <div className="text-[10px] text-slate-400 font-sans">{m.registration_id}</div>}</td>
                      <td className="px-4 py-2.5 font-mono text-slate-600">{m.template_name}</td>
                      <td className="px-4 py-2.5 text-slate-500 capitalize">{m.source}</td>
                      <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full font-medium ${MSG_STATUS[m.status] || MSG_STATUS.sent}`}>{m.status}</span>
                        {m.error && <div className="text-[10px] text-rose-600 mt-1 max-w-[220px]">{m.error}</div>}</td>
                      <td className="px-4 py-2.5 text-slate-400">{fmt(m.sent_at || m.created_at)}</td>
                      <td className="px-4 py-2.5 text-slate-400">{fmt(m.delivered_at)}</td>
                      <td className="px-4 py-2.5 text-slate-400">{fmt(m.read_at)}</td>
                      <td className="px-4 py-2.5">{m.clicks.length ? m.clicks.map((c, i) => <div key={i} className="text-violet-700">{c.button_text} <span className="text-slate-400">{fmt(c.at)}</span></div>) : <span className="text-slate-300">—</span>}</td>
                    </tr>
                  ))}
                  {data.items.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">No messages match these filters.</td></tr>}
                </tbody>
              </table>
            </div>
            {data.total > 50 && (
              <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-100 text-xs">
                <button className={`${btnSecondary} py-1.5 px-3`} disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="analytics-prev-page">Previous</button>
                <span className="text-slate-500">Page {page + 1} of {Math.ceil(data.total / 50)}</span>
                <button className={`${btnSecondary} py-1.5 px-3`} disabled={(page + 1) * 50 >= data.total} onClick={() => setPage(page + 1)} data-testid="analytics-next-page">Next</button>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
